import { useTransition, useCallback, useState, Fragment, useRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import { Loader2, TriangleAlertIcon } from 'lucide-react';
import { debounce } from 'lodash';

import { UserColumnsEnum, QueryOrderEnum, UserRoleEnum } from 'src/graphql/schema';
import { TransferCompanyOwnershipDocument } from 'src/graphql/actions/user/transferCompanyOwnership.mutation';
import { UsersDocument } from 'src/graphql/actions/user/users.query';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { Alert, AlertDescription } from 'src/components/ui/alert';
import {
  Combobox,
  ComboboxLabel,
  ComboboxInput,
  ComboboxContent,
  ComboboxList,
  ComboboxItem,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxCollection,
} from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormMessage, FormLabel } from '@/components/ui/form';
import { withSuspense } from 'src/hoc';

import { useUsers } from '../model';

const schema = z.object({
  userId: z
    .string({
      message: 'Select an admin to transfer ownership to.',
    })
    .min(1, 'Select an admin to transfer ownership to.'),
});

type IFormValues = z.infer<typeof schema>;

type IContainerProps = {
  ref: React.RefObject<{
    reset: () => void;
  } | null>;
};

const Container = withSuspense(({ ref }: IContainerProps) => {
  const [query, setQuery] = useState<string | undefined>();
  const [inputValue, setInputValue] = useState('');
  const [isPending, startTransition] = useTransition();
  const {
    ui: { closeDialog },
    refetchUsers,
  } = useUsers();

  const isSearchPending = isPending || inputValue !== (query ?? '');

  const form = useForm<IFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { userId: '' },
  });

  useImperativeHandle(
    ref,
    () => ({
      reset: form.reset,
    }),
    [],
  );

  const {
    data: { users },
  } = useSuspenseQuery(UsersDocument, {
    variables: {
      orderBy: UserColumnsEnum.Name,
      order: QueryOrderEnum.Asc,
      role: UserRoleEnum.Admin,
      ...(query && { queryFilter: query }),
    },
  });

  const [transferCompanyOwnership] = useMutation(TransferCompanyOwnershipDocument, {
    onCompleted: refetchUsers,
  });

  const onSubmit = async ({ userId }: IFormValues) => {
    try {
      const { data } = await transferCompanyOwnership({ variables: { userId } });
      if (data?.transferCompanyOwnership?.success) {
        toast.success('Account ownership transferred');
        closeDialog();
      } else {
        throw new ClientError(data?.transferCompanyOwnership?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const debouncedSetQuery = useCallback(
    debounce((value: string) => {
      startTransition(() => setQuery(value));
    }, 500),
    [startTransition],
  );

  const onInputValueChange = useCallback(
    (value: string, { reason }: { reason: string }) => {
      if (reason === 'item-press') return;

      setInputValue(value);
      debouncedSetQuery(value);
    },
    [debouncedSetQuery],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New account owner</FormLabel>
              <Combobox
                items={users.nodes}
                itemToStringLabel={(user) => user?.name ?? ''}
                onValueChange={(user: { id: string; name: string } | null) => field.onChange(user?.id)}
                onInputValueChange={onInputValueChange}
              >
                <FormControl>
                  <ComboboxInput placeholder="Select a new account owner" showClear />
                </FormControl>
                <ComboboxContent>
                  {isSearchPending ? (
                    <div className="flex justify-center p-2">
                      <Loader2 className="size-4 animate-spin" />
                    </div>
                  ) : (
                    <Fragment>
                      <ComboboxEmpty>No users found</ComboboxEmpty>
                      <ComboboxList>
                        <ComboboxGroup>
                          <ComboboxLabel>Org members</ComboboxLabel>
                          <ComboboxCollection>
                            {(user) => (
                              <ComboboxItem key={user.id} value={user}>
                                {user.name}
                              </ComboboxItem>
                            )}
                          </ComboboxCollection>
                        </ComboboxGroup>
                      </ComboboxList>
                    </Fragment>
                  )}
                </ComboboxContent>
              </Combobox>
              <FormMessage />
            </FormItem>
          )}
        />
        <AlertDialogFooter className="mt-4">
          <AlertDialogCancel size="sm">Cancel</AlertDialogCancel>
          <Button
            type="submit"
            variant="destructive"
            size="sm"
            disabled={form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
          >
            Transfer ownership
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
});

const TransferOwnershipDialog = () => {
  const formRef = useRef<{
    reset: () => void;
  }>(null);
  const {
    ui: { transferOwnershipDialogOpen, closeDialog },
  } = useUsers();

  const onOpenChange = (open: boolean) => {
    if (!open) {
      closeDialog();
      formRef?.current?.reset();
    }
  };

  return (
    <AlertDialog open={transferOwnershipDialogOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Transfer account ownership?</AlertDialogTitle>
          <AlertDialogDescription>
            The new owner will have full control of your organization, including billing, and will be added to all
            products as a Manager.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Alert className="bg-accent border-card-foreground has-[>svg]:gap-x-1.5">
          <TriangleAlertIcon />
          <AlertDescription>
            Your role will change to Admin, and you&apos;ll lose billing access. Your product permissions won&apos;t
            change. Only the new owner can reverse this.
          </AlertDescription>
        </Alert>
        <Container ref={formRef} />
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default TransferOwnershipDialog;
